import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

function CheckoutForm({ clientSecret, paymentId, token }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      // ✅ Step 1: Confirm payment with Stripe
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        setProcessing(false);
        return;
      }

      console.log('🔵 Confirming payment with Stripe...');
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + "/payment-success",
        },
        redirect: "if_required",
      });

      if (error) {
        console.error("❌ Stripe payment error:", error);
        setErrorMessage(error.message);
        setProcessing(false);
        return;
      }

      console.log('✅ Stripe payment confirmed:', paymentIntent.status);

      // ✅ Step 2: Confirm payment on backend
      if (paymentIntent.status === "succeeded") {
        console.log('🔵 Confirming payment on backend...');
        const response = await fetch(
          `http://localhost:30002/api/v1/payments/${paymentId}/confirm`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Backend confirmation failed");
        }

        const data = await response.json();
        console.log('✅ Backend confirmation successful:', data);

        // ✅ Navigate to success page
        navigate("/payment-success", {
          state: {
            payment: data.payment,
            message: "Payment successful!",
          },
        });
      } else {
        setErrorMessage(`Payment status: ${paymentIntent.status}`);
        setProcessing(false);
      }
    } catch (err) {
      console.error("❌ Payment processing error:", err);
      setErrorMessage(err.message || "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-center">Complete Payment</h2>

      <form onSubmit={handleSubmit}>
        <PaymentElement />

        {errorMessage && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{errorMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || processing}
          className={`w-full mt-6 py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
            !stripe || processing
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {processing ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : (
            "Pay Now"
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          🔒 Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}

export default CheckoutForm;